/**
 * Smart Redirect Service
 * 
 * Handles intelligent redirection based on device platform and app installation status.
 * Supports deep linking to mobile app with fallbacks to app stores or web version.
 */

class SmartRedirectService {
  constructor() {
    // App store URLs and deep link schemes
    this.config = {
      // Deep link scheme for the mobile app
      appScheme: 'parkwithparallel',
      
          // App store URLs (placeholder - replace with actual URLs when app is published)
    iosAppStoreUrl: 'https://apps.apple.com/ca/app/parallel-mobile/id6751863179', // Replace with actual App Store ID
    androidPlayStoreUrl: 'https://play.google.com/store/apps/details?id=com.parkwithparallel.app',
      
      // Web fallback URL
      webFallbackUrl: 'https://app.parkwithparallel.com',
      
      // Timeout for checking app installation (ms)
      appCheckTimeout: 2500,
    };
  }

  /**
   * Detect the device platform
   * @returns {string} 'iOS' | 'Android' | 'Desktop'
   */
  detectPlatform() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'iOS';
    } else if (/android/.test(userAgent)) {
      return 'Android';
    } else {
      return 'Desktop';
    }
  }

  /**
   * Check if we're running in a mobile browser
   * @returns {boolean}
   */
  isMobileBrowser() {
    const platform = this.detectPlatform();
    return platform === 'iOS' || platform === 'Android';
  }

  /**
   * Generate deep link URL for the mobile app
   * @param {string} lotId - The display lot ID
   * @returns {string} Deep link URL
   */
  generateDeepLink(lotId) {
    // Generate a unique token for this deeplink
    const uniqueToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    
    // In development, use Expo Go app
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    // Check if we're in development (localhost, 127.0.0.1, or local IP)
    if (hostname === 'localhost' || 
        hostname === '127.0.0.1' || 
        hostname.startsWith('192.168.') ||
        hostname.startsWith('10.') ||
        hostname.startsWith('172.')) {
      
      // Use the same IP as the current page for Expo
      const expoUrl = `exp://${hostname}:8081/--/lotid/${lotId}?token=${uniqueToken}`;
      console.log('Development mode - using Expo URL:', expoUrl);
      return expoUrl;
    }
    
    return `${this.config.appScheme}://lotid/${lotId}?token=${uniqueToken}`;
  }

  /**
   * Generate Android intent URL for app detection and fallback
   * @param {string} lotId - The display lot ID
   * @returns {string} Android intent URL
   */
  generateAndroidIntent(lotId) {
    const deepLink = this.generateDeepLink(lotId);
    const fallbackUrl = this.config.androidPlayStoreUrl;
    const hostname = window.location.hostname;
    
    // In development, use Expo Go
    if (hostname === 'localhost' || 
        hostname === '127.0.0.1' || 
        hostname.startsWith('192.168.') ||
        hostname.startsWith('10.') ||
        hostname.startsWith('172.')) {
      
      console.log('Development mode - using Expo Go intent');
      return `intent://lotid/${lotId}#Intent;` +
             `scheme=exp;` +
             `package=host.exp.exponent;` +
             `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};` +
             `end`;
    }
    
    return `intent://lotid/${lotId}#Intent;` +
           `scheme=${this.config.appScheme};` +
           `package=com.parallel.parking;` +
           `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};` +
           `end`;
  }

  /**
   * Attempt to open the mobile app with deep link
   * @param {string} lotId - The display lot ID
   * @param {Function} onFallback - Callback for fallback handling
   * @param {Function} onSuccess - Optional callback for successful app opening
   */
  async attemptAppOpen(lotId, onFallback, onSuccess) {
    const platform = this.detectPlatform();
    const deepLink = this.generateDeepLink(lotId);

    if (platform === 'Android') {
      // Android: Use intent scheme for better app detection
      const intentUrl = this.generateAndroidIntent(lotId);
      
      try {
        console.log('Attempting Android app open with intent:', intentUrl);
        window.location.href = intentUrl;
        
        // For Android, the intent handles fallback automatically
        if (onSuccess) {
          setTimeout(onSuccess, 500);
        }
      } catch (error) {
        console.error('Error opening Android app:', error);
        onFallback();
      }
    } else if (platform === 'iOS') {
      // iOS: Use Universal Links approach with timeout fallback
      let hasRedirected = false;
      
      // Set up fallback timer
      const fallbackTimer = setTimeout(() => {
        if (!hasRedirected) {
          console.log('iOS app not installed or failed to open, triggering fallback');
          onFallback();
        }
      }, this.config.appCheckTimeout);

      // Listen for visibility change (app opened)
      const handleVisibilityChange = () => {
        if (document.hidden) {
          hasRedirected = true;
          clearTimeout(fallbackTimer);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          if (onSuccess) {
            onSuccess();
          }
        }
      };

      // Listen for page blur (app opened)
      const handleBlur = () => {
        hasRedirected = true;
        clearTimeout(fallbackTimer);
        window.removeEventListener('blur', handleBlur);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (onSuccess) {
          onSuccess();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('blur', handleBlur);

      try {
        console.log('Attempting iOS app open with deep link:', deepLink);
        window.location.href = deepLink;
      } catch (error) {
        console.error('Error opening iOS app:', error);
        clearTimeout(fallbackTimer);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('blur', handleBlur);
        onFallback();
      }
    } else {
      // Desktop: Skip app attempt, go straight to fallback
      console.log('Desktop detected, skipping app attempt');
      onFallback();
    }
  }

  /**
   * Handle fallback redirection
   * @param {string} lotId - The display lot ID
   */
  handleFallback(lotId) {
    const platform = this.detectPlatform();
    
    if (platform === 'iOS') {
      console.log('Redirecting to iOS App Store');
      window.location.href = this.config.iosAppStoreUrl;
    } else if (platform === 'Android') {
      console.log('Redirecting to Google Play Store');
      window.location.href = this.config.androidPlayStoreUrl;
    } else {
      console.log('Redirecting to web fallback with lot ID');
      // For desktop, stay on current page and show web interface
      // The parent component will handle this by not redirecting
      window.location.href = `${this.config.webFallbackUrl}/lotid/${lotId}`;
    }
  }

  /**
   * Main smart redirect function
   * @param {string} lotId - The display lot ID
   * @param {Object} options - Configuration options
   * @param {Function} options.onWebFallback - Callback for web fallback (desktop)
   * @param {Function} options.onAppSuccess - Callback for successful app opening
   * @param {Function} options.onAppFallback - Callback for app store fallback
   */
  async performSmartRedirect(lotId, options = {}) {
    const {
      onWebFallback,
      onAppSuccess,
      onAppFallback
    } = options;

    const platform = this.detectPlatform();
    
    console.log('Smart redirect initiated:', {
      lotId,
      platform,
      isMobile: this.isMobileBrowser()
    });

    if (platform === 'Desktop') {
      // Desktop: Use web interface
      console.log('Desktop detected - using web interface');
      if (onWebFallback) {
        onWebFallback();
      }
      return;
    }

    // Mobile: Attempt to open app
    await this.attemptAppOpen(
      lotId,
      () => {
        // App not installed or failed to open - redirect to store
        console.log('App not available - redirecting to store');
        this.handleFallback(lotId);
        if (onAppFallback) {
          onAppFallback();
        }
      },
      () => {
        // App opened successfully
        console.log('App opened successfully');
        if (onAppSuccess) {
          onAppSuccess();
        }
      }
    );
  }

  /**
   * Check if the current environment supports smart redirect
   * @returns {boolean}
   */
  isSmartRedirectSupported() {
    // Check for basic browser APIs
    return typeof window !== 'undefined' && 
           typeof navigator !== 'undefined' &&
           typeof document !== 'undefined';
  }

  /**
   * Get debug information about the current environment
   * @returns {Object} Debug information
   */
  getDebugInfo() {
    return {
      platform: this.detectPlatform(),
      isMobile: this.isMobileBrowser(),
      userAgent: navigator.userAgent,
      isSupported: this.isSmartRedirectSupported(),
      config: this.config
    };
  }
}

// Export singleton instance
export const smartRedirectService = new SmartRedirectService();
export default smartRedirectService;
